import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Rocket,
  GraduationCap,
  TrendingUp,
  Beaker,
  Flame,
  Zap,
  Award,
  MessageCircle,
  Globe,
  Star,
  Lock,
  Share2,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  'rocket': Rocket,
  'graduation-cap': GraduationCap,
  'trending-up': TrendingUp,
  'beaker': Beaker,
  'flame': Flame,
  'zap': Zap,
  'award': Award,
  'message-circle': MessageCircle,
  'globe': Globe,
  'star': Star
};

const CATEGORY_COLORS = {
  milestone: 'from-ocean-blue to-sky-blue',
  engagement: 'from-coral to-orange-400',
  social: 'from-mint to-green-400',
  special: 'from-purple-500 to-pink-500'
};

const BadgesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, languageVersion } = useAuth();
  const [allBadges, setAllBadges] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Refetch badges when language changes
  useEffect(() => {
    loadBadges();
  }, [languageVersion]);

  const loadBadges = async () => {
    try {
      const [allResponse, userResponse] = await Promise.all([
        api.getAllBadges(),
        api.getUserBadges()
      ]);
      setAllBadges(allResponse.data.badges || []);
      setUserBadges(userResponse.data.badges || []);
    } catch (error) {
      console.error('Failed to load badges:', error);
      toast.error(t('badges.loadError', 'Could not load badges'));
    } finally {
      setLoading(false);
    }
  };

  const earnedBadgeIds = new Set(userBadges.map(b => b.id));
  const earnedCount = userBadges.length;
  const totalCount = allBadges.length;
  const progress = totalCount > 0 ? (earnedCount / totalCount) * 100 : 0;

  const handleShare = (badge) => {
    const shareText = t('badges.shareText', 
      `I just earned the "${badge.name}" badge on Minnow School! 🎉 #FounderJourney #MinnowSchool`,
      { badgeName: badge.name }
    );
    
    if (navigator.share) {
      navigator.share({
        title: t('badges.shareTitle', 'Achievement Unlocked!'),
        text: shareText,
      });
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success(t('badges.copiedToClipboard', 'Copied to clipboard!'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(user?.preferred_language || 'en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group badges by category
  const badgesByCategory = allBadges.reduce((acc, badge) => {
    const category = badge.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(badge);
    return acc;
  }, {});

  const categoryNames = {
    milestone: t('badges.categoryMilestone', 'Milestones'),
    engagement: t('badges.categoryEngagement', 'Engagement'),
    social: t('badges.categorySocial', 'Social'),
    special: t('badges.categorySpecial', 'Special')
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-gradient-to-r from-ocean-blue to-sky-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                {t('badges.title', 'Achievements')}
              </h1>
              <p className="text-xs text-white/70">
                {earnedCount} / {totalCount} {t('badges.badgesEarned', 'badges earned')}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Banner */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">{t('badges.yourProgress', 'Your Progress')}</span>
                <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Recently Earned */}
        {userBadges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-coral" />
              {t('badges.recentlyEarned', 'Recently Earned')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {userBadges.slice(0, 4).map((badge, index) => {
                const IconComponent = ICON_MAP[badge.icon] || Award;
                const gradientClass = CATEGORY_COLORS[badge.category] || 'from-gray-400 to-gray-600';
                
                return (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="relative overflow-hidden group">
                      <CardContent className="p-4 text-center">
                        <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${gradientClass} flex items-center justify-center mx-auto mb-3`}>
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="font-semibold text-sm mb-1">{badge.name}</h3>
                        <p className="text-xs text-muted-foreground">{formatDate(badge.earned_at)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleShare(badge)}
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Badges by Category */}
        {Object.entries(badgesByCategory).map(([category, badges]) => (
          <div key={category} className="mb-8">
            <h2 className="text-lg font-semibold mb-4">
              {categoryNames[category] || category}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {badges.map((badge, index) => {
                const isEarned = earnedBadgeIds.has(badge.id);
                const IconComponent = ICON_MAP[badge.icon] || Award;
                const gradientClass = CATEGORY_COLORS[badge.category] || 'from-gray-400 to-gray-600';
                const earnedBadge = userBadges.find(b => b.id === badge.id);
                
                return (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`relative ${!isEarned ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isEarned 
                            ? `bg-gradient-to-br ${gradientClass}` 
                            : 'bg-muted'
                        }`}>
                          {isEarned ? (
                            <IconComponent className="w-7 h-7 text-white" />
                          ) : (
                            <Lock className="w-6 h-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{badge.name}</h3>
                            {isEarned && (
                              <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 text-xs">
                                {t('badges.earned', 'Earned')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{badge.description}</p>
                          {isEarned && earnedBadge?.earned_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(earnedBadge.earned_at)}
                            </p>
                          )}
                        </div>
                        {isEarned && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleShare(badge)}
                            className="flex-shrink-0"
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BadgesPage;
